/**
 * Thank You Page - Free User Registration
 * URL: /welcome
 * Used for analytics tracking of free user registrations
 */
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Sparkles, BookOpen, Trophy, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";

export default function ThankYouRegistration({ user }) {
  const navigate = useNavigate();

  // Redirect to home if not logged in (page accessed directly)
  useEffect(() => {
    if (!user) {
      const timer = setTimeout(() => {
        navigate("/");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [user, navigate]);

  return (
    <div className="container mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl mx-auto text-center"
        >
          {/* Success Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <CheckCircle className="w-10 h-10 text-green-500" />
          </motion.div>

          {/* Heading */}
          <h1 className="text-4xl font-bold mb-4">
            Welcome to CodingDrills! 🎉
          </h1>
          
          <p className="text-xl text-muted-foreground mb-8">
            Your free account has been created successfully. You&apos;re ready to start your Python coding journey!
          </p>

          {/* Features Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-card border border-border rounded-xl p-6"
            >
              <BookOpen className="w-8 h-8 text-primary mx-auto mb-3" />
              <h3 className="font-semibold mb-2">Daily Questions</h3>
              <p className="text-sm text-muted-foreground">
                Practice with new coding challenges every day
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-card border border-border rounded-xl p-6"
            >
              <Sparkles className="w-8 h-8 text-primary mx-auto mb-3" />
              <h3 className="font-semibold mb-2">Free Drills</h3>
              <p className="text-sm text-muted-foreground">
                Access curated drills to build your skills
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-card border border-border rounded-xl p-6"
            >
              <Trophy className="w-8 h-8 text-primary mx-auto mb-3" />
              <h3 className="font-semibold mb-2">Track Progress</h3>
              <p className="text-sm text-muted-foreground">
                Earn points and climb the rankings
              </p>
            </motion.div>
          </div>

          {/* CTA Button */}
          <Button
            size="lg"
            onClick={() => navigate("/")}
            className="gap-2"
            data-testid="start-coding-btn"
          >
            Start Coding Now
            <ArrowRight className="w-4 h-4" />
          </Button>

          {/* Upgrade hint */}
          <p className="text-sm text-muted-foreground mt-6">
            Want unlimited access?{" "}
            <button
              onClick={() => navigate("/plans")}
              className="text-primary hover:underline"
            >
              Upgrade to Student Plan
            </button>
          </p>
        </motion.div>
      </div>
  );
}
